import { Calculator, ClipboardCheck, ShieldAlert } from 'lucide-react';
import { civilChecks } from '../lib/civilCheck';
import { materialRows } from '../lib/materialList';
import { computePit } from '../lib/excavation';
import { lvAmount } from '../lib/lv';
import { money } from '../lib/units';
import { MIN_COVER, tooFlat } from '../lib/model';
import { dailyTotals } from './DailyReport';
import DecIn from './DecIn';

// civil works: one cost overview (no double counting of LV positions taken over from the survey) + minimum cover per utility
export function costParts(project) {
  const reps = project.reports || [];
  const own = reps.flatMap((r) => r.lv || []).filter((p) => !/^(net|pit):/.test(p.src || ''));
  return [
    ['mat', materialRows(project).total],
    ['pits', (project.pits || []).reduce((a, p) => a + (computePit(p).total || 0), 0)],
    ['daily', reps.reduce((a, r) => a + dailyTotals(r.daily).total, 0)],
    ['lv', own.reduce((a, p) => a + lvAmount(p), 0)],
  ];
}

function CostBox({ t, project }) {
  const parts = costParts(project), sum = parts.reduce((a, [, v]) => a + v, 0);
  return (
    <div className="lv-box" data-testid="cost-summary">
      <div className="sub-title"><Calculator size={14} /> {t.csTitle}</div>
      {parts.map(([k, v]) => <div key={k} className="row cs-row" data-testid={`cost-${k}`}><span>{t['cs_' + k]}</span><b>{money(v)}</b></div>)}
      <div className="row cs-row cs-total" data-testid="cost-total"><span>{t.csTotal}</span><b>{money(sum)}</b></div>
      <p className="muted small-hint">{t.csHint}</p>
    </div>
  );
}

const ROWS = ['water', 'gas', 'power', 'power_ms', 'telecom', 'heat', 'sewer'];
function CoverBox({ t, project, updateProject }) {
  const o = project.minCover || {}, flat = (project.pipes || []).filter((p) => tooFlat(project, p));
  const set = (k, v) => updateProject((p) => ({ ...p, minCover: { ...(p.minCover || {}), [k]: v } }));
  return (
    <details className="lv-box" data-testid="min-cover-box" open={flat.length > 0}>
      <summary className="sub-title"><ShieldAlert size={14} /> {t.mcTitle} {flat.length > 0 && <b className="cover-warn" data-testid="min-cover-count">· {t.mcFlat.replace('{n}', flat.length)}</b>}</summary>
      {flat.map((p) => <p key={p.id} className="cover-warn" data-testid={`flat-${p.id}`}>{p.name}: {String(p.depth).replace('.', ',')} m &lt; {String(tooFlat(project, p)).replace('.', ',')} m</p>)}
      <div className="mc-grid">
        {ROWS.map((k) => (
          <label key={k} className="pit-num"><span>{k === 'power_ms' ? t.sub_ms : t['type_' + k]}</span>
            <DecIn data-testid={`min-cover-${k}`} value={o[k] ?? (k === 'power_ms' ? 0.8 : MIN_COVER[k])} onNum={(n) => set(k, n)} />
          </label>
        ))}
      </div>
      <p className="muted small-hint">{t.mcHint}</p>
    </details>
  );
}

function CheckBox({ t, project, updateProject }) {
  const items = civilChecks(project, t), open = items.filter((x) => !x.done).length;
  const tick = (id, v) => updateProject((p) => ({ ...p, civilOk: { ...(p.civilOk || {}), [id]: v ? Date.now() : undefined } }));
  return (
    <details className="lv-box" data-testid="civil-check-box" open={open > 0}>
      <summary className="sub-title"><ClipboardCheck size={14} /> {t.ckTitle} <b className={open ? 'cover-warn' : 'muted'} data-testid="civil-check-count">· {open ? t.ckOpenN.replace('{n}', open) : t.ckAllOk}</b></summary>
      {!items.length && <p className="muted small-hint" data-testid="civil-check-empty">{t.ckEmpty}</p>}
      {items.map((x) => (
        <label key={x.id} className={`chk-row ${x.done ? 'muted' : 'cover-warn'}`} data-testid={`civil-check-${x.id.replace(':', '-')}`}>
          <input type="checkbox" checked={x.done} onChange={(e) => tick(x.id, e.target.checked)} data-testid={`civil-check-input-${x.id.replace(':', '-')}`} />
          <span>{x.kind === 'pipe' ? '〰' : '▣'} <b>{x.name}</b>: {x.text}</span>
        </label>
      ))}
      <p className="muted small-hint">{t.ckHint}</p>
    </details>
  );
}

export default function CivilBox({ t, project, updateProject }) {
  return <><CostBox t={t} project={project} /><CoverBox t={t} project={project} updateProject={updateProject} /><CheckBox t={t} project={project} updateProject={updateProject} /></>;
}
